"use client";

import {
  useRef,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { AeraButton } from "@/app/components/ui/AeraButton";

const TEXTAREA_CLASS =
  "w-full py-3 border border-aera-lightgray/80 rounded-lg focus:border-aera-charcoal focus:outline-none text-aera-charcoal placeholder:text-aera-gray disabled:opacity-50 disabled:cursor-not-allowed resize-none overflow-hidden placeholder:whitespace-nowrap placeholder:overflow-hidden placeholder:text-ellipsis";

const DEFAULT_FILE_ACCEPT =
  ".xlsx,.htm,.csv,.gif,.pdf,.png,.jpeg,.html,.docx,.json,.jpg,.bmp,.txt";

function SendIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <div className="w-5 h-5 border-2 border-aera-white/30 border-t-aera-gray rounded-full animate-spin" />
  );
}

function AttachIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-aera-slate group-hover:text-aera-amber transition-colors duration-200 ease-in-out"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export type ChatInputProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder: string;
  disabled?: boolean;
  sendDisabled?: boolean;
  /** Active send button styling (charcoal background) */
  sendHighlighted?: boolean;
  /** Spinner on send button (e.g. while uploading) */
  isBusy?: boolean;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  /** Optional file attach (AgentChatPanel) */
  fileInputRef?: RefObject<HTMLInputElement | null>;
  onFileChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  attachDisabled?: boolean;
  attachBusy?: boolean;
  fileAccept?: string;
  fileMultiple?: boolean;
};

export function ChatInput({
  value,
  onChange,
  onSend,
  placeholder,
  disabled = false,
  sendDisabled = false,
  sendHighlighted,
  isBusy = false,
  textareaRef: textareaRefProp,
  fileInputRef,
  onFileChange,
  attachDisabled,
  attachBusy = false,
  fileAccept = DEFAULT_FILE_ACCEPT,
  fileMultiple = true,
}: ChatInputProps) {
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = textareaRefProp ?? internalTextareaRef;

  const hasAttach = onFileChange != null;
  const highlighted = sendHighlighted ?? Boolean(value.trim());

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !disabled && !sendDisabled) {
      e.preventDefault();
      onSend();
    }
  };

  const handleTextareaInput = (e: FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    target.style.height = "auto";
    target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
  };

  const handleAttachClick = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="relative flex items-center">
      {hasAttach ? (
        <>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple={fileMultiple}
            accept={fileAccept}
            onChange={onFileChange}
          />
          <AeraButton
            variant="ghost"
            size="sm"
            onClick={handleAttachClick}
            disabled={attachDisabled}
            className="absolute left-2 top-2.5 p-1! group"
            title="อัปโหลดไฟล์"
            icon={attachBusy ? <SpinnerIcon /> : <AttachIcon />}
          />
        </>
      ) : null}

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className={`${TEXTAREA_CLASS} ${hasAttach ? "pl-10 pr-12" : "pl-4 pr-12"}`}
        style={{ minHeight: "48px", maxHeight: "200px" }}
        onInput={handleTextareaInput}
      />

      <AeraButton
        variant="ghost"
        size="sm"
        onClick={onSend}
        disabled={sendDisabled}
        className={`absolute right-2 top-2.5 p-1! ${
          highlighted
            ? "bg-aera-charcoal text-aera-white hover:bg-aera-amber hover:text-aera-charcoal"
            : "text-aera-slate hover:bg-aera-white"
        }`}
        icon={isBusy ? <SpinnerIcon /> : <SendIcon />}
      />
    </div>
  );
}
